import { getIn } from 'immutable';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import type { ResourceActionCreator, ResourceFailureActionCreator } from 'app-components/flux/core/resourceStoreTypes';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import * as actions from './datasetsActions';
import { getLastSyncDate } from './datasetsSelectors';
import { transformResponseToDatasets } from './datasetsTransformers';
import { type DatasetProps, mkDataset } from './datasetsTypes';

const mkQcsSyncDatasetsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<DatasetProps>();
const mkResourceConfig = (
  successAction: ResourceActionCreator<DatasetProps>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncDatasetsConfig({
    resourceName: 'datasets',
    resourceBaseUrl: '/datasets',
    axiosConfig: { headers: { 'qcs-dataset-id': null } },
    getLastSyncDate,
    transformResponseToResources: transformResponseToDatasets,
    successAction,
    failureAction,
  });

// =================================================================================================
// Get Datasets
// =================================================================================================
export function* getDatasetsActionWatcher() {
  yield resourceSagaUtils.takeAndDebounce(
    actions.getDatasets,
    resourceSagaUtils.qcsSyncGetResources<DatasetProps>,
    mkResourceConfig(actions.getDatasetsSuccess, actions.getDatasetsFailure),
  );
}

// since QCS has no single endpoint to get dataset entitlements - we do it manually - https://quickenteam.atlassian.net/browse/QCS-15448
export function* getDatasetsSuccessActionWatcher() {
  yield takeEvery(actions.getDatasetsSuccess, function* _getDatasetsSuccess(getDatasetsSuccess) {
    yield all(
      getDatasetsSuccess?.payload.resources
        ?.filter((dataset) => !dataset.isDeleted)
        .map((dataset) =>
          call(
            resourceSagaUtils.qcsSyncGetResource<DatasetProps>,
            resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<DatasetProps>()({
              resourceName: 'datasets entitlements',
              resourceBaseUrl: `/datasets/${dataset.id}/entitlements`,
              axiosConfig: { headers: { 'qcs-dataset-id': null } },
              transformResponseToResource: (response) =>
                mkDataset({
                  id: dataset.id,
                  entitlements: response.data.entitlements,
                  headless: dataset.headless,
                  ownerId: dataset.ownerId,
                }),
              successAction: actions.getDatasetEntitlementsSuccess,
              failureAction: actions.getDatasetEntitlementsFailure,
            }),
          ),
        ),
    );
  });
}

// create
// =================================================================================================
export function* createDatasetActionWatcher() {
  yield takeEvery(
    actions.createDataset.type,
    resourceSagaUtils.qcsSyncCreateResource<DatasetProps>,
    mkResourceConfig(actions.createDatasetSuccess, actions.createDatasetFailure),
  );
}

function* createDatasetFailureActionWatcher() {
  yield takeLatest(actions.createDatasetFailure.type, function* createDatasetFailure() {
    yield put(actions.getDatasets());
  });
}

function* createDatasetSuccessActionWatcher() {
  yield takeLatest(actions.createDatasetSuccess, function* createDatasetSuccess(action) {
    const datasetId = action.payload.id;

    yield call(
      resourceSagaUtils.qcsSyncGetResource<DatasetProps>,
      resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<DatasetProps>()({
        resourceName: 'datasets entitlements',
        resourceBaseUrl: `/datasets/${datasetId}/entitlements`,
        axiosConfig: { headers: { 'qcs-dataset-id': null } },
        transformResponseToResource: (response) =>
          mkDataset({
            id: datasetId,
            entitlements: response.data.entitlements,
            headless: response.data.headless,
            ownerId: response.data.ownerId,
          }),
        successAction: actions.getDatasetEntitlementsSuccess,
        failureAction: actions.getDatasetEntitlementsFailure,
      }),
    );

    // we might set override for another dataset, so can't use updatePreferenceOverride action
    yield call(resourceSagaUtils.qcsRequest, {
      method: 'POST',
      path: 'v2/preferences/override',
      data: { preferenceName: 'gsfScreen', value: 'greeting' },
      headers: { 'qcs-dataset-id': datasetId },
    });
  });
}

// delete
// =================================================================================================
export function* deleteDatasetActionWatcher() {
  yield takeEvery(
    actions.deleteDataset.type,
    resourceSagaUtils.qcsSyncDeleteResource<DatasetProps>,
    mkResourceConfig(actions.deleteDatasetSuccess, actions.deleteDatasetFailure),
  );
}

function* deleteDatasetFailureActionWatcher() {
  yield takeLatest(actions.deleteDatasetFailure, function* deleteDatasetFailure() {
    yield put(actions.getDatasets());
  });
}

// update
// =================================================================================================
export function* updateDatasetActionWatcher() {
  yield takeEvery(
    actions.updateDataset.type,
    resourceSagaUtils.qcsSyncUpdateResource<DatasetProps>,
    mkResourceConfig(actions.updateDatasetSuccess, actions.updateDatasetFailure),
  );
}

function* updateDatasetFailureActionWatcher() {
  yield takeLatest(actions.updateDatasetFailure, function* updateDatasetFailure() {
    yield put(actions.getDatasets());
  });
}

function* datasetPollActionWatcher() {
  yield takeEvery(actions.datasetPoll, function* datasetPoll(action) {
    if (action.payload && action.payload.id) {
      const config = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<DatasetProps>()({
        resourceName: 'datasets',
        resourceBaseUrl: `/analysis/saga/${action.payload.id}`,
        successAction: actions.datasetPollSuccess,
        failureAction: actions.datasetPollFailure,
        axiosConfig: { headers: { 'qcs-dataset-id': null } },
        pollConfig: resourceStoreTypes.mkPollConfig({
          isComplete: (response) => Boolean(getIn(response, ['data', 'result', 'completedAt'])),
          pause: 1000,
          attempts: 10, // 1000 * 10 = 10sec
        }),
      });
      yield call(resourceSagaUtils.qcsSyncPollResource<DatasetProps>, config, action);
    }
  });
}

// for loading watcher sagas
export default [
  getDatasetsActionWatcher,
  getDatasetsSuccessActionWatcher,
  deleteDatasetActionWatcher,
  deleteDatasetFailureActionWatcher,
  createDatasetActionWatcher,
  createDatasetFailureActionWatcher,
  updateDatasetActionWatcher,
  updateDatasetFailureActionWatcher,
  datasetPollActionWatcher,
  createDatasetSuccessActionWatcher,
];
